import { Badge } from "@/components/ui/badge";
import { ORDER_STATUS_ORDER, getOrderStatusMeta } from "@/lib/orderStatus";
import { AlertCircle, ExternalLink, Eye, Star } from "lucide-react";
import AdminStatusBadge from "./AdminStatusBadge";
import { formatDate, getOrderId, getRemainingDays, isUrgent, orderLabel } from "./adminHelpers";

const OrderCard = ({ order, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(order)}
    className={`group w-full rounded-3xl border bg-[#18181B] p-5 text-left transition-colors hover:border-white/25 ${order.priority ? "border-[#CCFF00]/50" : isUrgent(order) ? "border-red-500/40" : "border-white/10"}`}
  >
    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      <div className="min-w-0">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <AdminStatusBadge status={order.status} />
          <Badge className="bg-white/10 text-white">{orderLabel(order.order_type)}</Badge>
          {order.priority && <Badge className="bg-[#CCFF00]/20 text-[#CCFF00]"><Star className="mr-1 h-3 w-3" />Priorité</Badge>}
          {!order.priority && isUrgent(order) && <Badge className="bg-red-500/20 text-red-400"><AlertCircle className="mr-1 h-3 w-3" />Urgente</Badge>}
        </div>
        <p className="text-lg font-black text-white">{order.user_name || "Client"}</p>
        <p className="mt-1 truncate text-sm text-zinc-500">{order.user_email}</p>
        <p className="mt-3 line-clamp-2 text-sm text-zinc-400">{order.specific_request || "Aucune demande détaillée."}</p>
      </div>
      <div className="shrink-0 text-left md:text-right">
        <p className="text-xs uppercase text-zinc-500">Commande</p>
        <p className="text-sm font-semibold text-white">{formatDate(order.created_at)}</p>
        {order.status !== "delivered" ? <p className="mt-2 text-sm text-[#CCFF00]">{getRemainingDays(order.deadline)}j restants</p> : <p className="mt-2 text-sm text-green-400">Livrée</p>}
      </div>
    </div>
    <div className="mt-5 flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-2 border border-white/20 px-3 py-2 text-xs font-bold uppercase text-white transition-colors group-hover:bg-white/5"><Eye className="h-3 w-3" />Ouvrir la fiche</span>
      {order.video_link && <span className="inline-flex items-center gap-2 border border-[#CCFF00]/30 px-3 py-2 text-xs font-bold uppercase text-[#CCFF00]"><ExternalLink className="h-3 w-3" />Vidéo</span>}
    </div>
  </button>
);

const AdminOrdersBoard = ({ orders, focusedStatus, setFocusedStatus, openOrder }) => {
  if (focusedStatus) {
    const meta = getOrderStatusMeta(focusedStatus);
    const focusedOrders = orders.filter((order) => order.status === focusedStatus);
    return (
      <section className={`rounded-3xl bg-[#111113] border ${meta.borderClass} min-h-[620px] overflow-hidden`}>
        <div className="p-5 border-b border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div><p className="text-xs text-zinc-500 uppercase">Vue détaillée</p><h2 className="text-2xl font-black text-white uppercase">{meta.pluralLabel}</h2></div>
          <button onClick={() => setFocusedStatus(null)} className="border border-white/20 text-white px-3 py-2 text-xs font-bold uppercase hover:bg-white/5 transition-colors">Retour Kanban</button>
        </div>
        <div className="p-5 space-y-4">
          {focusedOrders.length === 0 ? <p className="text-sm text-zinc-600">Aucune commande dans cette catégorie.</p> : focusedOrders.map((order) => <OrderCard key={getOrderId(order)} order={order} onOpen={openOrder} />)}
        </div>
      </section>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {ORDER_STATUS_ORDER.map((status) => {
        const meta = getOrderStatusMeta(status);
        const columnOrders = orders.filter((order) => order.status === status);
        return (
          <section key={status} className={`rounded-3xl bg-[#111113] border ${meta.borderClass} min-h-[420px] overflow-hidden`}>
            <button type="button" onClick={() => setFocusedStatus(status)} className="w-full p-4 border-b border-white/10 flex items-center justify-between hover:bg-white/[0.03] transition-colors"><h2 className="text-white font-black uppercase">{meta.pluralLabel}</h2><Badge className={meta.badgeClass}>{columnOrders.length}</Badge></button>
            <div className="p-3 space-y-3">{columnOrders.length === 0 ? <p className="text-sm text-zinc-600 p-4">Aucune commande.</p> : columnOrders.map((order) => <OrderCard key={getOrderId(order)} order={order} onOpen={openOrder} />)}</div>
          </section>
        );
      })}
    </div>
  );
};

export default AdminOrdersBoard;
